import { useQuery } from '@tanstack/react-query';
import {
  CAMADAS,
  formatarBRLCurto,
  mascararDocumento,
  resumirPainel,
  type Candidato,
} from '@aponte/shared';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/cliente.js';
import { Aviso, mensagemDoErro } from '../componentes/Aviso.js';
import { SeloDeEtapa } from '../componentes/SeloDeEtapa.js';
import { useSessao } from '../autenticacao/ContextoDeSessao.js';

/**
 * Painel: ocupação das 100 cadeiras e o funil de candidatos.
 *
 * Os números vêm do mesmo `resumirPainel` que o backend usa — calculado aqui sobre a
 * lista já carregada, para não fazer duas viagens até a API.
 */
export function Painel() {
  const navegar = useNavigate();
  const { temPapel } = useSessao();

  const consulta = useQuery({
    queryKey: ['candidatos'],
    queryFn: api.listarCandidatos,
  });

  if (consulta.isLoading) {
    return <div className="carregando">Carregando o funil…</div>;
  }

  if (consulta.isError) {
    return <Aviso mensagem={{ tom: 'erro', texto: mensagemDoErro(consulta.error) }} />;
  }

  const candidatos = consulta.data ?? [];
  const resumo = resumirPainel(candidatos);
  const larguraOcupadas = (resumo.ocupadas / resumo.totalDeCadeiras) * 100;
  const larguraReservadas = (resumo.preReservadas / resumo.totalDeCadeiras) * 100;

  return (
    <>
      <div className="indicadores">
        <Indicador numero={String(resumo.candidatosNoFunil)} legenda={'Candidatos\nno funil'} />
        <Indicador numero={String(resumo.preReservadas)} legenda={'Cadeiras\npré-reservadas'} />
        <Indicador numero={String(resumo.ocupadas)} legenda={'Cadeiras\nocupadas'} />
        <Indicador
          numero={formatarBRLCurto(resumo.receitaContratada)}
          legenda={'Receita\ncontratada'}
        />
      </div>

      <div className="ocupacao">
        <div className="titulo">Ocupação das {resumo.totalDeCadeiras} cadeiras</div>
        <div className="trilho">
          <i className="ocupada" style={{ width: `${larguraOcupadas}%` }} />
          <i className="reservada" style={{ width: `${larguraReservadas}%` }} />
        </div>
        <div className="legenda-ocupacao">
          <span>
            <i className="ponto" style={{ background: 'var(--ok)' }} />
            Ocupadas <b>{resumo.ocupadas}</b>
          </span>
          <span>
            <i className="ponto" style={{ background: 'var(--atencao)' }} />
            Pré-reservadas <b>{resumo.preReservadas}</b>
          </span>
          <span>
            <i className="ponto livre" />
            Disponíveis <b>{resumo.disponiveis}</b>
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {Object.values(resumo.porCamada)
              .map((c) => `${c.nome} ${c.ocupadas}/${c.total}`)
              .join(' · ')}
          </span>
        </div>
      </div>

      <div className="secao">
        <h2>
          Funil de <em>candidatos</em>
        </h2>
        {temPapel('closer') && (
          // Abre a ficha em branco. O candidato só passa a existir no servidor quando o
          // closer salvar — clicar aqui e desistir não deixa registro nem sujeita o funil.
          <button className="bt" onClick={() => navegar('/candidatos/novo')}>
            + Novo candidato
          </button>
        )}
      </div>

      {candidatos.length === 0 ? (
        <div className="vazio">
          Nenhum candidato ainda.
          <br />
          Comece por <b>+ Novo candidato</b>.
        </div>
      ) : (
        <TabelaDoFunil candidatos={candidatos} />
      )}
    </>
  );
}

function Indicador({ numero, legenda }: { numero: string; legenda: string }) {
  return (
    <div className="indicador">
      <div className="numero">{numero}</div>
      <div className="legenda" style={{ whiteSpace: 'pre-line' }}>
        {legenda}
      </div>
    </div>
  );
}

function TabelaDoFunil({ candidatos }: { candidatos: Candidato[] }) {
  const navegar = useNavigate();

  return (
    <table className="funil">
      <thead>
        <tr>
          <th>Empresa ou pessoa</th>
          <th>Camada</th>
          <th>Cadeira</th>
          <th>Etapa</th>
          <th style={{ textAlign: 'right' }}>Valor</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {candidatos.map((c) => (
          <tr key={c.candidatoId}>
            <td className="empresa">
              {c.razao || <span style={{ color: 'var(--apagado)' }}>sem nome</span>}
              <small>
                {c.doc ? mascararDocumento(c.doc) : '—'} ·{' '}
                {c.tipo === 'pf' ? 'pessoa física' : 'pessoa jurídica'}
              </small>
            </td>
            <td>{CAMADAS[c.camada].nome}</td>
            <td>{c.numero ? `N.º ${c.numero}` : '—'}</td>
            <td>
              <SeloDeEtapa etapa={c.etapa} />
            </td>
            <td className="valor">{formatarBRLCurto(CAMADAS[c.camada].valor)}</td>
            <td style={{ textAlign: 'right' }}>
              <button
                className="bt vazado pequeno"
                onClick={() => navegar(`/candidatos/${c.candidatoId}`)}
              >
                Abrir
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
